import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, EmptyState, ListRow } from "../../../design-v2";
import useAuthBootstrap from "../../../auth/useAuthBootstrap";
import { formatPerformedValue } from "../../workoutLog/domain/loadDisplay";
import { formatTrainingDate, workoutDetailsPath } from "../../workoutLog/domain/workoutDetails";
import { isWorkoutLogEnabled } from "../../workoutLog/domain/featureFlag";
import { getDeviceTimezone, getExerciseSessions } from "../api/insightsApi";
import { ChevronRightIcon } from "./InsightsChrome";
import { DETAIL_NO_SESSIONS, formatNotComparedReason } from "../domain/exercisePerformancePresentation";

/*
 * Exercise Detail sessions (EP blueprint §6.5 item 4, §6.6). The first page
 * arrives with the detail response; "Load more" pages come from the sessions
 * endpoint with the opaque `nextCursor` it returned, appended in this
 * component only (not cached, §6.2). Every page belongs to one key (signed-in
 * user, exercise, timeframe, timezone): a response that lands after the key
 * changed is dropped, so a page is never appended under another selection.
 * Sets are shown as entered, with the classes the API computed.
 */

// One compact line of the performed sets, as entered (Workout Log formatting).
export function formatSessionSets(session) {
  return (session.sets || [])
    .map((set) => formatPerformedValue({
      measurementType: set.reps == null && set.seconds != null ? "TIMED_HOLD" : "REPS",
      load: set.load && set.load.value != null ? set.load : null,
      reps: set.reps,
      seconds: set.seconds,
      rir: set.rir,
    }))
    .filter(Boolean)
    .join(", ");
}

function SessionRow({ session, linkable }) {
  const reason = formatNotComparedReason(session);
  const title = (
    <span className="lz-insights-ep-session__title">
      <span>{formatTrainingDate(session.trainingDate)}</span>
      {session.extra ? <Badge tone="neutral">Extra</Badge> : null}
    </span>
  );
  const description = (
    <>
      {session.title ? <span className="lz-insights-ep-session__workout">{session.title}</span> : null}
      <span className="lz-insights-ep-session__sets">{formatSessionSets(session)}</span>
    </>
  );
  const props = {
    className: "lz-insights-ep-row lz-insights-ep-session",
    title,
    description,
    meta: reason,
    "data-testid": `ep-session-${session.executionId}`,
  };
  // Workout Details exists only with the Workout Log on (§6.6); ownership is
  // enforced by its endpoint (404 for another user's execution).
  return linkable
    ? <ListRow {...props} as={Link} to={workoutDetailsPath(session.executionId)} trailing={<ChevronRightIcon />} />
    : <ListRow {...props} />;
}

export default function ExerciseSessionsList({ exerciseId, timeframe, firstPage }) {
  const identity = useAuthBootstrap().user?.id ?? null;
  const timezone = getDeviceTimezone();
  const key = JSON.stringify([identity, exerciseId, timeframe, timezone]);
  const [more, setMore] = useState({ key, items: [], cursor: undefined, status: "idle" });
  const keyRef = useRef(key);
  keyRef.current = key;

  // A new key (or a new first page) starts over from the first page.
  const current = more.key === key ? more : { key, items: [], cursor: undefined, status: "idle" };
  useEffect(() => {
    setMore({ key, items: [], cursor: undefined, status: "idle" });
  }, [key, firstPage]);

  const cursor = current.cursor === undefined ? firstPage?.nextCursor ?? null : current.cursor;
  const seen = new Set();
  const items = [...(firstPage?.items || []), ...current.items].filter((session) => {
    if (seen.has(session.executionId)) return false;
    seen.add(session.executionId);
    return true;
  });

  const loadMore = useCallback(() => {
    if (!cursor) return;
    const requestKey = key;
    setMore((previous) => ({ ...(previous.key === requestKey ? previous : { key: requestKey, items: [], cursor }), status: "loading" }));
    getExerciseSessions(exerciseId, timeframe, timezone, cursor).then(
      (page) => {
        if (keyRef.current !== requestKey) return;
        setMore((previous) => (previous.key !== requestKey ? previous : {
          key: requestKey,
          items: [...previous.items, ...(page?.items || [])],
          cursor: page?.nextCursor ?? null,
          status: "idle",
        }));
      },
      () => {
        if (keyRef.current !== requestKey) return;
        setMore((previous) => (previous.key !== requestKey ? previous : { ...previous, status: "error" }));
      }
    );
  }, [cursor, exerciseId, key, timeframe, timezone]);

  if (!items.length) {
    return <EmptyState className="lz-insights-empty" titleAs="h3" title="No sessions" body={DETAIL_NO_SESSIONS} data-testid="ep-no-sessions" />;
  }

  const linkable = isWorkoutLogEnabled();
  return (
    <div className="lz-insights-ep-sessions" data-testid="ep-sessions">
      <div className="lz-insights-ep-rows">
        {items.map((session) => <SessionRow key={session.executionId} session={session} linkable={linkable} />)}
      </div>
      {current.status === "error" ? (
        <div className="lz-insights-error" role="alert">
          <p className="lz-insights-error__text">We couldn't load more sessions right now.</p>
          <Button variant="secondary" size="sm" onClick={loadMore}>Try again</Button>
        </div>
      ) : null}
      {cursor && current.status !== "error" ? (
        <Button
          variant="secondary"
          className="lz-insights-ep-more"
          onClick={loadMore}
          isLoading={current.status === "loading"}
          loadingLabel="Loading more sessions"
        >
          Load more
        </Button>
      ) : null}
    </div>
  );
}
